import PropTypes from 'prop-types';
import { useEffect, useState, useContext } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import Select from 'react-select';
import * as Yup from 'yup';

// CONTEXTS
import { ModalContext } from 'contexts/ModalContext';

// ASSETS
import { AiOutlineSave, AiOutlineDelete } from 'react-icons/ai';
import { FiAlertCircle } from 'react-icons/fi';
import axios from 'axios';

// ==== VALIDACIONS (mateixes que el modal d'importació, adaptades a l'admin) ====
const dniRegex = /^[0-9]{8}[A-Z]$/;
const nieRegex = /^[XYZ][0-9]{7}[A-Z]$/;
const passportRegex = /^[A-Z0-9]{5,10}$/i;

const validationSchema = Yup.object({
  NOM: Yup.string()
    .required('Requerit')
    .min(3, 'Massa curt, revisa si falten dades')
    .max(100, 'Longitud excessiva')
    .test('no-double-spaces', 'No pot contenir espais dobles', (v) => !v || !/\s{2,}/.test(v)),

  DNI: Yup.string()
    .required('Requerit')
    .max(15, 'Longitud excessiva, revisa el camp')
    .test('no-spaces', 'No pot contenir espais', (v) => !v || !/\s/.test(v))
    .test('uppercase', "Ha d'estar en majúscules", (v) => !v || v === v.toUpperCase())
    .test('dni-nie-pass', 'Ha de ser un DNI, NIE o Passaport vàlid', (value) => {
      if (!value) return false;
      return dniRegex.test(value) || nieRegex.test(value) || passportRegex.test(value);
    }),

  DATA_NAIXAMENT: Yup.date().required('Requerit').typeError('Data no vàlida'),

  ID_ESCOLA: Yup.number().required('Requerit').typeError('Requerit'),
  ID_PROFESSOR: Yup.number().required('Requerit').typeError('Requerit'),

  INICI_PRACTIQUES: Yup.date()
    .required('Requerit')
    .typeError('Data no vàlida')
    .test(
      'after-birth',
      "La data de naixement és posterior a l'inici de pràctiques",
      function (v) {
        const { DATA_NAIXAMENT } = this.parent;
        if (!v || !DATA_NAIXAMENT) return true;
        return new Date(DATA_NAIXAMENT) <= new Date(v);
      }
    )
    .test(
      'min-age-16',
      "L'alumne ha de tenir com a mínim 16 anys a l'inici de les pràctiques",
      function (v) {
        const { DATA_NAIXAMENT } = this.parent;
        if (!v || !DATA_NAIXAMENT) return true;
        const dNaix = new Date(DATA_NAIXAMENT);
        const dInici = new Date(v);
        let edat = dInici.getFullYear() - dNaix.getFullYear();
        const encaraNoFaAnys =
          dInici.getMonth() < dNaix.getMonth() ||
          (dInici.getMonth() === dNaix.getMonth() && dInici.getDate() < dNaix.getDate());
        if (encaraNoFaAnys) edat -= 1;
        return edat >= 16;
      }
    )
    .test(
      'max-age-100',
      "L'edat supera els 100 anys a l'inici de les pràctiques, revisa la data de naixement",
      function (v) {
        const { DATA_NAIXAMENT } = this.parent;
        if (!v || !DATA_NAIXAMENT) return true;
        const dNaix = new Date(DATA_NAIXAMENT);
        const dInici = new Date(v);
        let edat = dInici.getFullYear() - dNaix.getFullYear();
        const encaraNoFaAnys =
          dInici.getMonth() < dNaix.getMonth() ||
          (dInici.getMonth() === dNaix.getMonth() && dInici.getDate() < dNaix.getDate());
        if (encaraNoFaAnys) edat -= 1;
        return edat <= 100;
      }
    ),

  FI_PRACTIQUES: Yup.date()
    .required('Requerit')
    .typeError('Data no vàlida')
    .test(
      'after-inici',
      "La data de fi ha de ser posterior a la d'inici de pràctiques",
      function (v) {
        const { INICI_PRACTIQUES } = this.parent;
        if (!v || !INICI_PRACTIQUES) return true;
        return new Date(v) >= new Date(INICI_PRACTIQUES);
      }
    ),

  INICI_CHECK: Yup.number().oneOf([0, 1, 2]).required('Requerit'),
  FI_CHECK: Yup.number().oneOf([0, 1, 2]).required('Requerit'),
});

const StudentsFormDetail = ({ fields, handleSubmit, handleDelete, isNew, initialConflict }) => {
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [teacherOptions, setTeacherOptions] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(fields.ID_ESCOLA || null);
  const [ssPart1, setSsPart1] = useState(fields.NUM_SS ? fields.NUM_SS.slice(0, 2) : '');
  const [ssPart2, setSsPart2] = useState(fields.NUM_SS ? fields.NUM_SS.slice(2, 10) : '');
  const [ssPart3, setSsPart3] = useState(fields.NUM_SS ? fields.NUM_SS.slice(10, 12) : '');
  const [conflictError, setConflictError] = useState(null);
  const [ssError, setSsError] = useState(null);

  const { openModal } = useContext(ModalContext);

  // Mostrar el conflicte detectat en obrir el formulari (només si no s'ha modificat encara)
  useEffect(() => {
    if (initialConflict) {
      setConflictError({
        conflictType: initialConflict.conflictType,
        conflicts: initialConflict.conflicts,
        isInitial: true, // marcador per diferenciar-lo del que apareix en guardar
      });
    }
  }, [initialConflict]);

  useEffect(() => {
    if (fields.NUM_SS) {
      setSsPart1(fields.NUM_SS.slice(0, 2));
      setSsPart2(fields.NUM_SS.slice(2, 10));
      setSsPart3(fields.NUM_SS.slice(10, 12));
    }
  }, [fields.NUM_SS]);

  const handleDeleteModal = (id) => {
    openModal({ id, handleDelete, type: 'DELETE' });
  };

  useEffect(() => {
    const fetchSchools = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL + '/schools/');
        const options = response.data.map((school) => ({
          value: school.ID_ESCOLA,
          label: `${school.CODI} - ${school.NOM}`,
        }));
        setSchoolOptions(options);
      } catch (error) {
        console.error('Error fetching schools:', error);
      }
    };

    const fetchTeachers = async () => {
      try {
        const response = await axios.get(process.env.REACT_APP_API_URL + '/teachers');
        setTeacherOptions(response.data);
      } catch (error) {
        console.error('Error fetching teachers:', error);
      }
    };

    fetchSchools();
    fetchTeachers();
  }, []);

  const formatDate = (date) => {
    if (!date) return null;
    const d = new Date(date);
    const month = `0${d.getMonth() + 1}`.slice(-2);
    const day = `0${d.getDate()}`.slice(-2);
    return `${d.getFullYear()}-${month}-${day}`;
  };

  // Format dd/mm/aaaa per mostrar les dates dels conflictes al banner
  const formatDateDisplay = (value) => {
    if (!value) return '—';
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!match) return value;
    const [, y, m, d] = match;
    return `${d}/${m}/${y}`;
  };

  const initialValues = {
    ...fields,
    DATA_NAIXAMENT: formatDate(fields.DATA_NAIXAMENT),
    FI_PRACTIQUES: isNew ? '' : formatDate(fields.FI_PRACTIQUES),
    INICI_PRACTIQUES: isNew ? '' : formatDate(fields.INICI_PRACTIQUES),
    INICI_CHECK: isNew ? 2 : fields.INICI_CHECK !== undefined ? fields.INICI_CHECK : 0,
    FI_CHECK: isNew ? 2 : fields.FI_CHECK !== undefined ? fields.FI_CHECK : 0,
  };

  // Envoltori del handleSubmit del pare: valida SS, concatena parts, i captura conflictes
  const onFormSubmit = async (values, { setSubmitting }) => {
    setConflictError(null);
    setSsError(null);

    // Validació del NUM_SS (les 3 parts concatenades)
    const fullSSNumber = `${ssPart1}${ssPart2}${ssPart3}`;
    if (!/^\d{12}$/.test(fullSSNumber)) {
      setSsError('El Número de Seguretat Social ha de tenir 12 dígits numèrics');
      setSubmitting(false);
      return;
    }

    const updatedValues = { ...values, NUM_SS: fullSSNumber };

    try {
      await handleSubmit(updatedValues);
    } catch (err) {
      if (err?.conflictType) {
        setConflictError(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize={true}
      validateOnMount={true}
      initialValues={initialValues}
      onSubmit={onFormSubmit}
      validationSchema={validationSchema}
    >
      {({ values, errors, touched, setFieldValue, isSubmitting }) => (
        <Form className="flex flex-col bg-blue-100 border-2 border-indigo-800 rounded-lg p-6">
          <div className="grid grid-cols-12 gap-6">

            {/* ==== BANNER DE CONFLICTE ==== */}
            {conflictError && (
              <div
                className={`col-span-12 p-4 border-2 rounded-lg ${
                  conflictError.conflictType === 'overlap'
                    ? 'bg-red-50 border-red-300'
                    : 'bg-amber-50 border-amber-300'
                }`}
              >
                <div className="flex items-start gap-3">
                  <FiAlertCircle
                    className={`flex-shrink-0 mt-0.5 ${
                      conflictError.conflictType === 'overlap' ? 'text-red-600' : 'text-amber-600'
                    }`}
                    size={22}
                  />
                  <div className="flex-1">
                    <p
                      className={`text-sm font-semibold mb-2 ${
                        conflictError.conflictType === 'overlap' ? 'text-red-800' : 'text-amber-800'
                      }`}
                    >
                      {conflictError.isInitial
                        ? conflictError.conflictType === 'overlap'
                          ? 'Atenció: aquest alumne té el DNI duplicat i el període de pràctiques es solapa amb un altre'
                          : 'Atenció: aquest alumne té el DNI duplicat i el període acaba i comença el mateix mes que un altre'
                        : conflictError.conflictType === 'overlap'
                          ? 'No es pot guardar: alumne amb DNI duplicat i període de pràctiques solapat'
                          : 'No es pot guardar: alumne amb DNI duplicat, el període acaba i comença el mateix mes que un altre'}
                    </p>
                    <div
                      className={`text-xs space-y-1 ${
                        conflictError.conflictType === 'overlap' ? 'text-red-700' : 'text-amber-700'
                      }`}
                    >
                      {conflictError.conflicts?.map((c, i) => (
                        <p key={i}>
                          · Ja existeix amb <strong>codi becari {c.codiBecari}</strong>
                          {c.nom ? ` (${c.nom})` : ''} — pràctiques del{' '}
                          {formatDateDisplay(c.inici)} al {formatDateDisplay(c.fi)}
                        </p>
                      ))}
                    </div>
                    <p
                      className={`text-xs mt-2 italic ${
                        conflictError.conflictType === 'overlap' ? 'text-red-600' : 'text-amber-600'
                      }`}
                    >
                      {conflictError.isInitial
                        ? 'Si modifiqueu les dades i guardeu, es tornaran a validar.'
                        : 'Reviseu el DNI o les dates de pràctiques abans de tornar a guardar.'}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {/* ==== FI BANNER ==== */}

            <div className="col-span-12">
              <h2 className="text-lg font-semibold text-indigo-900 mb-4">Informació Bàsica</h2>
              <div className="grid grid-cols-12 gap-6 bg-white border-2 border-blue-200 p-4 rounded-lg">
                {!isNew && (
                  <div className="col-span-1">
                    <label className="block text-base font-bold text-indigo-800 mb-1">Codi Becari</label>
                    <div className="w-full p-2 border rounded-sm border-gray-300 bg-gray-100 text-gray-700">
                      {values.CODI_BECARI || '—'}
                    </div>
                  </div>
                )}

                <div className="col-span-7">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="NOM">Cognoms i Nom</label>
                  <Field
                    className={`w-full p-2 border rounded-sm ${
                      touched.NOM && errors.NOM ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'
                    }`}
                    name="NOM"
                    type="text"
                  />
                  <ErrorMessage name="NOM" component="div" className="text-red-600 text-sm" />
                </div>

                <div className="col-span-4">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="DNI">DNI</label>
                  <Field
                    className={`w-full p-2 border rounded-sm ${
                      (touched.DNI && errors.DNI) || conflictError
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'
                    }`}
                    name="DNI"
                    type="text"
                  />
                  <ErrorMessage name="DNI" component="div" className="text-red-600 text-sm" />
                </div>

                <div className="col-span-6">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="DATA_NAIXAMENT">Data Naixement</label>
                  <Field
                    className={`w-full p-2 border rounded-sm ${
                      touched.DATA_NAIXAMENT && errors.DATA_NAIXAMENT ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'
                    }`}
                    name="DATA_NAIXAMENT"
                    type="date"
                  />
                  <ErrorMessage name="DATA_NAIXAMENT" component="div" className="text-red-600 text-sm" />
                </div>

                <div className="col-span-2">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="SS_PART1">Numero Seguretat Social</label>
                  <Field
                    className={`w-full p-2 border rounded-sm ${ssError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'}`}
                    name="SS_PART1"
                    minLength="2"
                    maxLength="2"
                    placeholder="00"
                    value={ssPart1}
                    onChange={(e) => setSsPart1(e.target.value)}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="SS_PART2">00000000</label>
                  <Field
                    className={`w-full p-2 border rounded-sm ${ssError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'}`}
                    name="SS_PART2"
                    minLength="8"
                    maxLength="8"
                    placeholder="00000000"
                    value={ssPart2}
                    onChange={(e) => setSsPart2(e.target.value)}
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="SS_PART3">00</label>
                  <Field
                    className={`w-full p-2 border rounded-sm ${ssError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'}`}
                    name="SS_PART3"
                    minLength="2"
                    maxLength="2"
                    placeholder="00"
                    value={ssPart3}
                    onChange={(e) => setSsPart3(e.target.value)}
                  />
                </div>
                {ssError && (
                  <div className="col-span-12 -mt-4">
                    <div className="text-red-600 text-sm">{ssError}</div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-12">
              <h2 className="text-lg font-semibold text-indigo-900 mb-4">Escola</h2>
              <div className="grid grid-cols-12 gap-6 bg-white border-2 border-blue-200 p-4 rounded-lg">
                <div className="col-span-6">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="ID_ESCOLA">Escola</label>
                  <Select
                    className="w-full rounded-sm text-grey-dark"
                    name="ID_ESCOLA"
                    onChange={(e) => {
                      setFieldValue('ID_ESCOLA', e.value);
                      setSelectedSchool(e.value);
                      setFieldValue('ID_PROFESSOR', null);
                    }}
                    placeholder="Seleccionar..."
                    options={schoolOptions}
                    value={schoolOptions.find((option) => option.value === values.ID_ESCOLA)}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        padding: '2px 0px 2px 0px',
                        border: state.isFocused ? '1px solid #9ca3af' : '1px solid #d1d5db',
                        boxShadow: state.isFocused ? 'none' : 'none',
                        '&:hover': { border: '1px solid #9ca3af' },
                        borderRadius: '0.125rem',
                        cursor: 'pointer',
                      }),
                      option: (baseStyles, state) => ({
                        ...baseStyles,
                        color: '#333333',
                        backgroundColor: state.isFocused && '#d1d5db',
                        '&:hover': { backgroundColor: '#f3f4f6' },
                        cursor: 'pointer',
                      }),
                    }}
                  />
                  <ErrorMessage name="ID_ESCOLA" component="div" className="text-red-600 text-sm" />
                </div>
                <div className="col-span-6">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="ID_PROFESSOR">Professor</label>
                  <Select
                    className="w-full rounded-sm text-grey-dark"
                    name="ID_PROFESSOR"
                    onChange={(e) => setFieldValue('ID_PROFESSOR', e.value)}
                    placeholder="Seleccionar..."
                    options={teacherOptions
                      .filter((teacher) => teacher.ID_ESCOLA === selectedSchool)
                      .map((teacher) => ({
                        value: teacher.ID_PROFESSOR,
                        label: `${teacher.NOM} ${teacher.COGNOM}`,
                      }))}
                    value={
                      teacherOptions
                        .filter((teacher) => teacher.ID_ESCOLA === selectedSchool)
                        .map((teacher) => ({
                          value: teacher.ID_PROFESSOR,
                          label: `${teacher.NOM} ${teacher.COGNOM}`,
                        }))
                        .find((option) => option.value === values.ID_PROFESSOR) || null
                    }
                    getOptionLabel={(option) => option.label}
                    getOptionValue={(option) => option.value}
                    styles={{
                      control: (baseStyles, state) => ({
                        ...baseStyles,
                        padding: '2px 0px 2px 0px',
                        border: state.isFocused ? '1px solid #9ca3af' : '1px solid #d1d5db',
                        boxShadow: state.isFocused ? 'none' : 'none',
                        '&:hover': { border: '1px solid #9ca3af' },
                        borderRadius: '0.125rem',
                        cursor: 'pointer',
                      }),
                      option: (baseStyles, state) => ({
                        ...baseStyles,
                        color: '#333333',
                        backgroundColor: state.isFocused && '#d1d5db',
                        '&:hover': { backgroundColor: '#f3f4f6' },
                        cursor: 'pointer',
                      }),
                    }}
                  />
                  <ErrorMessage name="ID_PROFESSOR" component="div" className="text-red-600 text-sm" />
                </div>
              </div>
            </div>

            <div className="col-span-12">
              <h2 className="text-lg font-semibold text-indigo-900 mb-4">Pràctiques</h2>
              <div className="grid grid-cols-12 gap-6 bg-white border-2 border-blue-200 p-4 rounded-lg">
                <div className="col-span-6">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="INICI_PRACTIQUES">Inici Pràctiques</label>
                  <Field
                    className={`w-full p-2 border rounded-sm ${
                      (touched.INICI_PRACTIQUES && errors.INICI_PRACTIQUES) || conflictError
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'
                    }`}
                    name="INICI_PRACTIQUES"
                    type="date"
                  />
                  <ErrorMessage name="INICI_PRACTIQUES" component="div" className="text-red-600 text-sm" />

                  <label className="block text-base font-bold text-indigo-800 mb-1 mt-4" htmlFor="FI_PRACTIQUES">Fi Pràctiques</label>
                  <Field
                    className={`w-full p-2 border rounded-sm ${
                      (touched.FI_PRACTIQUES && errors.FI_PRACTIQUES) || conflictError
                        ? 'border-red-300 bg-red-50'
                        : 'border-gray-300 hover:border-gray-400 focus:border-gray-400'
                    }`}
                    name="FI_PRACTIQUES"
                    type="date"
                  />
                  <ErrorMessage name="FI_PRACTIQUES" component="div" className="text-red-600 text-sm" />
                </div>
                <div className="col-span-6">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="INICI_CHECK">Check Tasca Inici</label>
                  <Field as="select" name="INICI_CHECK" className="w-full p-2 border rounded-sm border-gray-300 hover:border-gray-400 focus:border-gray-400">
                    <option value={1}>Sí</option>
                    <option value={0}>No</option>
                    <option value={2}>Pendent</option>
                  </Field>
                  <ErrorMessage name="INICI_CHECK" component="div" className="text-red-600 text-sm" />

                  <label className="block text-base font-bold text-indigo-800 mb-1 mt-4" htmlFor="FI_CHECK">Check Tasca Final</label>
                  <Field as="select" name="FI_CHECK" className="w-full p-2 border rounded-sm border-gray-300 hover:border-gray-400 focus:border-gray-400">
                    <option value={1}>Sí</option>
                    <option value={0}>No</option>
                    <option value={2}>Pendent</option>
                  </Field>
                  <ErrorMessage name="FI_CHECK" component="div" className="text-red-600 text-sm" />
                </div>
              </div>
            </div>

            <div className="col-span-12">
              <h2 className="text-lg font-semibold text-indigo-900 mb-4">Observacions</h2>
              <div className="grid grid-cols-12 gap-6 bg-white border-2 border-blue-200 p-4 rounded-lg">
                <div className="col-span-12">
                  <label className="block text-base font-bold text-indigo-800 mb-1" htmlFor="OBSERVACIONS">Comentaris</label>
                  <Field
                    as="textarea"
                    className="w-full p-2 border rounded-sm border-gray-300 hover:border-gray-400 focus:border-gray-400"
                    name="OBSERVACIONS"
                  />
                  <ErrorMessage name="OBSERVACIONS" component="div" className="text-red-600 text-sm" />
                </div>
              </div>
            </div>
          </div>

          <div className="grid w-full grid-cols-12 pt-4 m-0 gap-y-4">
            <div className="col-start-1 col-end-13 shadow md:col-start-3 md:col-end-11 lg:col-start-4 lg:col-end-10 xl:col-start-5 xl:col-end-9">
              <button
                alt="GUARDAR"
                title="GUARDAR"
                disabled={isSubmitting}
                className="flex flex-row items-center justify-center w-full col-span-1 p-2 text-white transition duration-300 bg-green-600 border border-green-700 rounded-sm-sm hover:border-green-300 hover:bg-indigo-200 hover:text-green-700 font-ms-semi disabled:opacity-50"
                type="submit"
              >
                <AiOutlineSave size={25} className="mr-2" />
                <p className="text-base font-semibold">Guardar</p>
              </button>
            </div>

            <div className="col-start-1 col-end-13 shadow md:col-start-3 md:col-end-11 lg:col-start-4 lg:col-end-10 xl:col-start-5 xl:col-end-9">
              {!isNew && (
                <button
                  alt="ELIMINAR"
                  title="ELIMINAR"
                  className="flex flex-row items-center justify-center w-full p-2 text-white transition duration-300 bg-red-600 border border-red-700 rounded-sm-sm hover:border-red-300 hover:bg-red-200 hover:text-red-700 font-ms-semi"
                  onClick={() => handleDeleteModal(values.ID_ALUMNES)}
                  type="button"
                >
                  <AiOutlineDelete size={25} className="mr-2" />
                  <p className="text-base font-semibold">Eliminar</p>
                </button>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

StudentsFormDetail.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  fields: PropTypes.object,
  isNew: PropTypes.bool,
   initialConflict: PropTypes.object,
};

export default StudentsFormDetail;